import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, KeyRound, RefreshCw, Info, Check, X } from "lucide-react";
import api from "@/lib/api";

const field = "w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none";
const err = (e, fb) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);
const btn = "inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-40";
const TRIGGER = { manual: "Manuell", automation: "Automation", oauth: "Verbindung" };

export default function TVConnection({ onChanged }) {
  const [cfg, setCfg] = useState(null);
  const [token, setToken] = useState("");
  const [devices, setDevices] = useState([]);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, l] = await Promise.all([api.get("/admin/tv/settings"), api.get("/admin/tv/logs")]);
    setCfg(c.data); setLogs(l.data);
  };
  useEffect(() => { load().catch(() => toast.error("Einstellungen nicht ladbar")); }, []);

  const saveToken = async () => {
    try { const { data } = await api.put("/admin/tv/settings", { pat_token: token, auth_mode: "pat" }); setCfg(data); setToken(""); toast.success("Token gespeichert"); } catch (e) { toast.error(err(e, "Fehler")); }
  };
  const loadDevices = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/tv/devices"); setDevices(data); toast.success(`${data.length} Geräte gefunden`); } catch (e) { toast.error(err(e, "Geräte nicht abrufbar")); } finally { setBusy(false); }
  };
  const choose = async (d) => {
    try {
      const { data } = await api.put("/admin/tv/settings", { device_id: d.device_id, device_label: d.label });
      setCfg(data); toast.success(`„${d.label}“ ausgewählt`); onChanged();
    } catch (e) { toast.error(err(e, "Auswahl fehlgeschlagen")); }
  };
  const connect = async () => {
    try { const { data } = await api.get("/admin/tv/oauth/start"); window.location.href = data.url; } catch (e) { toast.error(err(e, "OAuth nicht verfügbar")); }
  };
  const setMode = async (auth_mode) => {
    try { const { data } = await api.put("/admin/tv/settings", { auth_mode }); setCfg(data); } catch (e) { toast.error(err(e, "Fehler")); }
  };

  if (!cfg) { return <div className="card-lux p-8 text-center text-[#A8A398]">Lade …</div>; }
  const connected = cfg.auth_mode === "oauth" ? cfg.oauth_connected : cfg.pat_set && !cfg.pat_expired;
  const badge = !connected ? "Nicht verbunden" : cfg.auth_mode === "oauth" ? "Verbunden" : "Token hinterlegt";

  return (
    <div className="space-y-5" data-testid="tv-connection">
      <div className="card-lux p-4 flex gap-3 text-xs text-[#F5F0E6] border-[#D4AF37]/30">
        <Info size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />
        <div>Damit der TV per API aus dem Standby angeht, muss am TV <b>„Mit Mobilgerät einschalten“</b> aktiv sein (Einstellungen → Allgemein → Externe Geräteverwaltung).</div>
      </div>

      <div className="card-lux p-5 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] flex-1">Verbindungsstatus</div>
          <span data-testid="tv-conn-status" className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border ${connected ? "border-[#2F6B3F] text-[#7FD49A]" : "border-[#7A1921] text-[#EE9CA1]"}`}>
            {badge}
          </span>
        </div>
        <div className="text-sm text-[#A8A398]">Modus: <span className="text-[#F5F0E6]" data-testid="tv-conn-mode">{cfg.auth_mode === "oauth" ? "OAuth 2.0 (automatische Verlängerung)" : "Persönlicher Token"}</span>
          {cfg.device_label && <> · TV: <span className="text-[#F5F0E6]" data-testid="tv-conn-device">{cfg.device_label}</span></>}</div>

        {cfg.oauth_available ? (
          <div className="flex gap-2 flex-wrap">
            <button onClick={connect} className={btn} data-testid="tv-oauth-connect"><Link2 size={13} /> {cfg.oauth_connected ? "Neu verbinden" : "Mit SmartThings verbinden"}</button>
            {cfg.oauth_connected && cfg.auth_mode !== "oauth" && <button onClick={() => setMode("oauth")} className={btn} data-testid="tv-mode-oauth">OAuth verwenden</button>}
            {cfg.auth_mode === "oauth" && <button onClick={() => setMode("pat")} className={btn} data-testid="tv-mode-pat">Token verwenden</button>}
          </div>
        ) : (
          <div className="text-[11px] text-[#A8A398]" data-testid="tv-oauth-unavailable">OAuth ist noch nicht eingerichtet – bis dahin den persönlichen Token nutzen.</div>
        )}

        {cfg.auth_mode !== "oauth" && (
          <div className="space-y-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#A8A398]">Persönlicher Token {cfg.pat_set && <span className="normal-case tracking-normal">· hinterlegt{cfg.pat_expired ? " (vermutlich abgelaufen)" : ""}</span>}</div>
            <div className="flex gap-2">
              <input type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder={cfg.pat_set ? "•••••••• (neuen Token einfügen)" : "Token von account.smartthings.com/tokens"} className={field} data-testid="tv-pat-input" autoComplete="off" />
              <button onClick={saveToken} disabled={!token.trim()} className={btn} data-testid="tv-pat-save"><KeyRound size={13} /> Speichern</button>
            </div>
            <div className="text-[11px] text-[#A8A398]">Hinweis: Persönliche Tokens laufen nach 24 Stunden ab. Der Token wird nur auf dem Server gespeichert.</div>
          </div>
        )}
      </div>

      <div className="card-lux p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] flex-1">TV auswählen</div>
          <button onClick={loadDevices} disabled={busy} className={btn} data-testid="tv-devices-load"><RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Geräte laden</button>
        </div>
        {devices.map((d) => (
          <button key={d.device_id} onClick={() => choose(d)} data-testid={`tv-device-${d.device_id}`}
            className={`w-full text-left px-4 py-3 rounded border flex items-center gap-3 ${cfg.device_id === d.device_id ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] hover:border-[#D4AF37]"}`}>
            <div className="flex-1"><div className="text-sm">{d.label}</div><div className="text-[10px] text-[#A8A398] font-mono">{d.type || d.name}</div></div>
            {cfg.device_id === d.device_id && <Check size={15} className="text-[#D4AF37]" />}
          </button>
        ))}
      </div>

      <div className="card-lux p-5">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3">Protokoll</div>
        {!logs.length && <div className="text-xs text-[#A8A398]">Noch keine Einträge.</div>}
        <div className="divide-y divide-[#1A1A1A]" data-testid="tv-logs">
          {logs.map((l) => (
            <div key={l.id} className="py-2 flex items-start gap-3 text-xs">
              {l.ok ? <Check size={13} className="text-[#7FD49A] mt-0.5" /> : <X size={13} className="text-[#EE9CA1] mt-0.5" />}
              <div className="w-20 sm:w-28 shrink-0 text-[#A8A398]">{new Date(l.at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}</div>
              <div className="flex-1 min-w-0 break-all"><span className="text-[#EEDC82]">{TRIGGER[l.trigger] || l.trigger}</span> · {l.action}{l.detail && <span className="text-[#EE9CA1]"> – {l.detail}</span>}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
